/* Оболочка кабинета: левое меню и шапка — точь-в-точь как в прототипе (nav.js, ui.js renderTop).
   Название страницы живёт в шапке; страница задаёт его через usePageTitle. */
import { useCatalog } from '@tg/app-core';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { daysBetween, humanDate, ini, plural, today } from '../lib/format';
import { useClients, useGroups, useMe, useTemplates } from '../lib/data';
import { ICON } from '../ui/icons';
import { installTooltips } from '../ui/tooltips';
import { MBack, MobileNav, MRailBackdrop, MRailHead, useIsMobile, useMobileRail, type NavItem } from './Mobile';
import { useSupport } from './Support';
import { NetPill } from '../offline/Status';
import { ReadOnlyBand } from './ReadOnly';

type Title = { title: string; count?: number | null; extra?: ReactNode };
const TitleCtx = createContext<(t: Title) => void>(() => {});
/** Заголовок с элементом после текста (дата в конструкторе) */
export const useTitleSetter = () => useContext(TitleCtx);
export function usePageTitle(title: string, count?: number | null) {
  const set = useContext(TitleCtx);
  useEffect(() => {
    set({ title, count });
    document.title = title + ' · Тренерграм';
  }, [set, title, count]);
}

const BRAND = 'ТРЕНЕРГРАМ';
export const Logo = ({ onClick }: { onClick?: () => void }) => (
  <span className="logo" role="img" aria-label={BRAND} onClick={onClick}>
    <i>
      <b>{BRAND[0]}</b>
      {BRAND.slice(1, -1)}
      <b>{BRAND.slice(-1)}</b>
    </i>
    <em>ТМ</em>
  </span>
);


function readMin() {
  try {
    return !!localStorage.getItem('tg.navmin');
  } catch {
    return false;
  }
}

/** Разделы кабинета — одни на левое меню и на мобильную панель со шторкой «Ещё» */
function useNav(): NavItem[] {
  const me = useMe().data;
  const cs = useClients().data;
  const gs = useGroups().data;
  const cat = useCatalog();
  const tb = useTemplates('block').data;
  const tw = useTemplates('workout').data;
  const tp = useTemplates('program').data;
  return [
    { g: 'Работа' },
    { h: '/dashboard', k: 'dash', n: 'Дашборд' },
    { h: '/calendar', k: 'cal', n: 'Календарь', a: 'Кален' },
    { h: '/clients', k: 'users', n: 'Клиенты', a: 'Клиен', c: () => cs?.length ?? me?.used },
    { h: '/groups', k: 'grp', n: 'Группы', a: 'Группы', c: () => gs?.length },
    { g: 'Базы шаблонов' },
    { h: '/exercises', k: 'dumb', n: 'Упражнения', a: 'Упраж', c: () => (cat.ready ? cat.list.length : undefined) },
    { h: '/blocks', k: 'folder', n: 'Блоки', a: 'Блоки', c: () => tb?.length },
    { h: '/workouts', k: 'tpl', n: 'Тренировки', a: 'Трен', c: () => tw?.length },
    { h: '/programs', k: 'prog', n: 'Программы', a: 'Прогр', c: () => tp?.length },
  ];
}

function Nav({ NAV, name }: { NAV: NavItem[]; name: string }) {
  const [min, setMin] = useState(readMin);
  /* Виджет чата (кружок) грузится тут; пункт меню скрыт ниже */
  useSupport();
  useEffect(() => {
    document.body.classList.toggle('navmin', min);
    try {
      localStorage.setItem('tg.navmin', min ? '1' : '');
    } catch {
      /* хранилище недоступно */
    }
  }, [min]);
  return (
    <aside className="nav" id="nav">
      <div className="nh">
        <Logo onClick={() => min && setMin(false)} />
        <button className="navtog" id="navtog" title="Свернуть меню" aria-label="Свернуть меню" onClick={() => setMin(true)}>
          <ICON.back />
        </button>
      </div>
      <div className="nbody">
        {NAV.map((x, i) => {
          if ('g' in x)
            return (
              <div className="ngrp" key={i}>
                {x.g}
              </div>
            );
          const I = ICON[x.k];
          const c = x.c?.();
          return (
            <NavLink key={x.h} to={x.h} end={x.h === '/dashboard'} className={({ isActive }) => (isActive ? 'on' : '')} title={x.n}>
              <I />
              <span className="ntxt">{x.n}</span>
              <i className="nab">{x.a || x.n}</i>
              {x.c && <span className="cnt">{c ?? ''}</span>}
            </NavLink>
          );
        })}
        {/* Пункт «Поддержка» скрыт по просьбе заказчика — чат открывается кружком в углу (Support.tsx).
        {sup && (
          <a
            href="#"
            className="nsup"
            title="Поддержка — чат с командой Тренерграма"
            onClick={(e) => {
              e.preventDefault();
              sup.open();
            }}
          >
            <ICON.chat />
            <span className="ntxt">Поддержка</span>
            <i className="nab">Помощь</i>
            {sup.dot && <span className="sdot" />}
          </a>
        )}
        */}
      </div>
      <Link className="nfoot" to="/profile" title={name}>
        <span className="av">{ini(name)}</span>
        <span>
          <b>{name}</b>
        </span>
      </Link>
    </aside>
  );
}

/* Пробный период — в шапке на каждой странице, пока он идёт: тариф меняют в профиле. Так же — отменённая
   подписка (до какого числа доступ) и закончившаяся (только просмотр, по нажатию — продлить). */
function TrialPill() {
  const me = useMe().data;
  if (!me?.planUntil) return null;
  const sub = me.sub;
  if (sub.ended)
    return (
      <Link className="trial end" to="/profile#billing" title="Продлить подписку">
        Только просмотр · продлить
      </Link>
    );
  if (sub.canceled && !sub.trial)
    return (
      <Link className="trial" to="/profile#billing" title="Возобновить подписку">
        Подписка отменена · доступ до {humanDate(me.planUntil)}
      </Link>
    );
  if (!sub.trial) return null;
  const n = daysBetween(today(), me.planUntil);
  return (
    <Link className="trial" to="/profile#billing" title="Выбрать тариф">
      Пробный период · {n === 0 ? 'последний день' : 'осталось ' + n + ' ' + plural(n, 'день', 'дня', 'дней')}
    </Link>
  );
}

function Topbar({ t }: { t: Title }) {
  return (
    <header className="topbar" id="topbar">
      <MBack />
      <h1 className="ptitle" id="ptitle">
        {t.title}
        {t.count != null && <span className="cnt">{t.count}</span>}
        {t.extra}
      </h1>
      <span className="sp"></span>
      <NetPill />
      <TrialPill />
      <span className="tsplit one">
        <Link className="btn" to="/constructor">
          Создать тренировку
        </Link>
      </span>
    </header>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [t, setT] = useState<Title>({ title: '' });
  useEffect(installTooltips, []);
  const NAV = useNav();
  const me = useMe().data;
  const name = me ? [me.first, me.last].filter(Boolean).join(' ') : '';
  return (
    <TitleCtx.Provider value={setT}>
      <div className="app">
        <Nav NAV={NAV} name={name} />
        <div className="main">
          <Topbar t={t} />
          <ReadOnlyBand />
          <div className="page">{children}</div>
        </div>
      </div>
      <MobileNav nav={NAV} ini={ini(name)} name={name} />
    </TitleCtx.Provider>
  );
}

/** Правая панель списков сворачивается в колонку; состояние — в localStorage (nav.js initRail) */
export function useRailMin() {
  const [min, setMin] = useState(() => {
    try {
      return !!localStorage.getItem('tg.railmin');
    } catch {
      return false;
    }
  });
  useEffect(() => {
    document.body.classList.toggle('railmin', min);
    try {
      localStorage.setItem('tg.railmin', min ? '1' : '');
    } catch {
      /* хранилище недоступно */
    }
    return () => document.body.classList.remove('railmin');
  }, [min]);
  return [min, setMin] as const;
}

/** Каркас списка с правой панелью: .cons > main > .pane + aside.rail */
export function RailLayout({ minLabel, title, body, foot, sheetKey, children }: { minLabel: string; title: string; body: ReactNode; foot?: ReactNode; sheetKey?: string | null; children: ReactNode }) {
  const [min, setMin] = useRailMin();
  /* На телефоне панель — шторка снизу: открывается выбором строки списка (sheetKey) */
  const mobile = useIsMobile();
  const [sheet, setSheet] = useMobileRail();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) return void (first.current = false);
    if (mobile && sheetKey) setSheet(true);
  }, [sheetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="cons">
      <main>
        <section className="pane" id="page">
          {children}
        </section>
      </main>
      <MRailBackdrop onClose={() => setSheet(false)} />
      <aside className="rail" data-min={minLabel}>
        <MRailHead title={title} onClose={() => setSheet(false)} />
        <button className="railtog" id="railtog" title={min ? 'Развернуть панель' : 'Свернуть панель'} aria-label="Свернуть панель" onClick={() => setMin(!min)}></button>
        <div className="railhead">
          <div className="railttl" id="railttl">
            {title}
          </div>
        </div>
        <div className="railbody" id="railbody">
          {body}
        </div>
        <div className="railfoot" id="railfoot">
          {foot}
        </div>
      </aside>
    </div>
  );
}
